import { call, put, takeLatest, cancelled } from "redux-saga/effects";
import { fetchWorkflowHistoriesRequest, fetchWorkflowHistoriesSuccess, fetchWorkflowHistoriesFailure } from "../slices/workflows/workflowHistoriesSlice";
import { getWorkflowHistories, getPendingTasks, getWorkflowsList } from "../../api/workflowsApi";
import { fetchPendingTasksRequest, fetchPendingTasksSuccess, fetchPendingTasksFailure } from "../slices/workflows/pendingTasksSlice";
import { fetchworkflowListRequest, fetchworkflowListSuccess, fetchworkflowListFailure } from "../slices/workflows/workflowListSlice";
import { logger } from "helpers/logger";

function* fetchWorkflowHistoriesSaga(action) {
	const { assignee, platform, selectedCompany, enableModifyStatus, pageNumber, recordsByPage, searchParam, startDate, endDate, sortField, sortDirection } = action.payload;

	// ✅ Create AbortController
	const abortController = new AbortController();

	try {
		logger.info(`🚀 Fetching workflow histories for page ${pageNumber}`);

		const response = yield call(
			getWorkflowHistories,
			assignee,
			selectedCompany,
			enableModifyStatus,
			pageNumber,
			recordsByPage,
			searchParam,
			startDate,
			endDate,
			sortField,
			sortDirection,
			abortController.signal // ✅ Pass signal to API
		);

		logger.info(`✅ Successfully fetched page ${pageNumber}`);
		yield put(fetchWorkflowHistoriesSuccess(response));
	} catch (error) {
		// ✅ Check if error is from abort
		if (error.name === "AbortError") {
			logger.info(`🚫 Request cancelled for page ${pageNumber}`);
			return; // Don't dispatch failure for cancelled requests
		}

		logger.error(`❌ Failed to fetch page ${pageNumber}:`, error);
		yield put(fetchWorkflowHistoriesFailure(error.message));
	} finally {
		// ✅ Abort if saga is cancelled by takeLatest
		if (yield cancelled()) {
			logger.info(`🛑 Saga cancelled for page ${pageNumber}, aborting request`);
			abortController.abort();
		}
	}
}

function* fetchPendingTasksSaga(action) {
	const { assignee,
		pageNumber,
		recordsByPage,
		searchParam,
		startDate,
		endDate,
		sortField,
		sortDirection,
		unassignedOnly,
		candidateGroups,
		includeFormData, } = action.payload;

	// ✅ Create AbortController
	const abortController = new AbortController();

	try {
		logger.info(`🚀 Fetching pending tasks for page ${pageNumber}`);

		const response = yield call(
			getPendingTasks,
			assignee,
			pageNumber,
			recordsByPage,
			searchParam,
			startDate,
			endDate,
			sortField,
			sortDirection,
			unassignedOnly,
			candidateGroups,
			includeFormData,
			abortController.signal // ✅ Pass signal to API
		);
		logger.info(`✅ Successfully fetched page ${pageNumber}`);
		yield put(fetchPendingTasksSuccess(response));
	} catch (error) {
		// ✅ Check if error is from abort
		if (error.name === "AbortError") {
			logger.info(`🚫 Request cancelled for page ${pageNumber}`);
			return; // Don't dispatch failure for cancelled requests
		}
		logger.error(`❌ Failed to fetch page ${pageNumber}:`, error);
		yield put(fetchPendingTasksFailure(error.message));
	} finally {
		// ✅ Abort if saga is cancelled by takeLatest
		if (yield cancelled()) {
			logger.info(`🛑 Saga cancelled for page ${pageNumber}, aborting request`);
			abortController.abort();
		}
	}
}

function* fetchWorkflowListSaga(action) {
	const { userId, companyId } = action.payload;

	// ✅ Create AbortController
	const abortController = new AbortController();

	try {
		logger.info("🚀 Fetching workflow list");

		const response = yield call(
			getWorkflowsList,
			userId,
			companyId,
			abortController.signal // ✅ Pass signal to API
		);
		logger.info("✅ Successfully fetched workflow list");
		yield put(fetchworkflowListSuccess(response));
	} catch (error) {
		// ✅ Check if error is from abort
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return; // Don't dispatch failure for cancelled requests
		}
		logger.error("❌ Failed to fetch data", error);
		yield put(fetchworkflowListFailure(error.message));
	} finally {
		// ✅ Abort if saga is cancelled by takeLatest
		if (yield cancelled()) {
			logger.info("🛑 Saga cancelled, aborting request");
			abortController.abort();
		}
	}
}

export default function* workflowsSaga() {
	yield takeLatest(fetchWorkflowHistoriesRequest.type, fetchWorkflowHistoriesSaga);
	yield takeLatest(fetchPendingTasksRequest.type, fetchPendingTasksSaga);
	yield takeLatest(fetchworkflowListRequest.type, fetchWorkflowListSaga);
}
